import type { ItemFieldRow } from './item-field-reader';
import type { FormulaReadDependencies } from './formula-read-context';
import { createFormulaReadContext } from './formula-read-context';
import { readCreatureStatuses } from './creature-status-reader';
import { readInventoryField } from './inventory-raw-readers';
import { integerCompileWorkingRow, integerCompileResult } from './integer-compute-slots';
import { phpJsonString } from './php-json-values';

type Row = Record<string, unknown>;
const arrayLike = (v: unknown): v is ItemFieldRow => v !== null && typeof v === 'object';
const own = (v: unknown, key: string): unknown => arrayLike(v) && Object.prototype.hasOwnProperty.call(v, key) ? (v as Row)[key] : undefined;
const valid = (v: string): boolean => /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}(?:\n)?(?![\s\S])/i.test(v);
const requireArray = (v: unknown): ItemFieldRow => { if (!arrayLike(v)) throw new TypeError('Compute source cleanup requires a PHP array'); return v; };
const stringArg = (v: unknown): string => { if (v == null || arrayLike(v)) throw new TypeError('Source identity requires a scalar string'); return phpJsonString(v); };
const strictEqual = (a: unknown, b: unknown): boolean => {
    if (!arrayLike(a) || !arrayLike(b)) return a === b;
    const left = Object.entries(a), right = Object.entries(b);
    return left.length === right.length && left.every(([key, value], i) => key === right[i]![0] && strictEqual(value, right[i]![1]));
};
export interface ComputeSourceCleanupDependencies extends FormulaReadDependencies {
    resolveItemName: (id: string) => unknown;
    queryItemType: (id: string) => unknown;
    /** Optional definition-read batching. It cannot change identity truth. */
    prewarmItemIdentities?: (ids: readonly string[]) => void;
}
/** One instance per compile request. Only definition identity types are memoized;
 * status/inventory names are resolved from the current carrier on every call.
 */
export function createComputeSourceCleaner(deps: ComputeSourceCleanupDependencies) {
    const readers = createFormulaReadContext(deps), types = new Map<string, string>();
    const reference = (id: string): ItemFieldRow | null => { deps.trace?.(['reference', id]); return deps.resolveCarrierRef(id); };
    function blobName(host: ItemFieldRow, id: string): string {
        const catalog = own(host, 'catalog'), hit = own(catalog, id) ?? own(catalog, id.toUpperCase()) ?? own(catalog, id.toLowerCase());
        let value: unknown;
        if (arrayLike(hit)) value = own(hit, 'item_name') != null || own(hit, 'item_type') != null ? own(hit, 'item_name') : own(own(hit, 'items'), 'item_name');
        const name = phpJsonString(value ?? '');
        if (name !== '') return name;
        deps.trace?.(['name', id]); return stringArg(deps.resolveItemName(id));
    }
    function name(rawId: string, input: ItemFieldRow): string | null {
        const id = stringArg(rawId), host = requireArray(input);
        if (id === '' || !valid(id)) return null;
        for (const mode of ['target', 'caster'] as const) for (const row of Object.values(readCreatureStatuses(host, mode, reference))) {
            if (!arrayLike(row)) continue;
            let sid = own(row, 'status_id'); if (arrayLike(sid)) sid = own(sid, '0');
            if (typeof sid === 'string' && sid.toLowerCase() === id.toLowerCase()) {
                const value = readers.itemField(row, 'item_name'); if (typeof value === 'string' && value !== '') return value;
            }
        }
        let hit: ItemFieldRow | null = null;
        function walk(list: unknown): void {
            if (!arrayLike(list) || hit !== null) return;
            for (const row of Object.values(list)) {
                if (!arrayLike(row)) continue;
                const iid = readInventoryField(row, 'inventory_id');
                if (typeof iid === 'string' && iid.toLowerCase() === id.toLowerCase()) { hit = row; return; }
                walk(own(row, 'inventory_in_container'));
            }
        }
        walk(own(host, 'inventory')); walk(own(host, 'inventory_equipped_arrs'));
        if (hit !== null) { const value = readers.itemField(hit, 'item_name'); if (typeof value === 'string' && value !== '') return value; }
        const value = blobName(host, id); return value !== '' && value !== 'Na' ? value : null;
    }
    function type(rawId: string, input: ItemFieldRow | null = null): string {
        const id = stringArg(rawId), host = input === null ? null : requireArray(input);
        if (id === '' || !valid(id)) return '';
        const key = id.toUpperCase(); if (types.has(key)) return types.get(key)!;
        const catalog = own(host, 'catalog'), hit = own(catalog, id) ?? own(catalog, id.toUpperCase()) ?? own(catalog, id.toLowerCase());
        if (arrayLike(catalog) && arrayLike(hit)) {
            const value = readers.itemField(hit, 'item_type');
            if (typeof value === 'string' && value !== '') { types.set(key, value); return value; }
        }
        deps.trace?.(['item-type', id]); const value = phpJsonString(deps.queryItemType(id)); types.set(key, value); return value;
    }
    function clean(input: ItemFieldRow, hostInput: ItemFieldRow): ItemFieldRow {
        const rows = integerCompileWorkingRow(requireArray(input)), host = requireArray(hostInput);
        const hrefs = Object.values(rows).filter(arrayLike).map(row => own(row, 'href_id')).filter((v): v is string => typeof v === 'string' && v !== '');
        if (hrefs.length && deps.prewarmItemIdentities) { deps.trace?.(['prewarm-identities', hrefs]); deps.prewarmItemIdentities(hrefs); }
        for (const [key, row] of Object.entries(rows)) {
            if (!arrayLike(row)) continue;
            const out: Row = { ...row }; rows[key] = out;
            const category = own(row, 'category'), kind = own(row, 'kind');
            if (category != null && kind != null && strictEqual(category, kind)) delete out.category;
            const rawHref = own(row, 'href_id'), href = typeof rawHref === 'string' ? rawHref : '';
            if (href === '') delete out.href_id;
            if (own(row, 'label') != null && href !== '' && name(href, host) === phpJsonString(own(row, 'label'))) delete out.label;
            if (kind != null) { const resolved = type(href, host); if (resolved !== '' && resolved === phpJsonString(kind)) delete out.kind; }
        }
        return integerCompileResult(rows);
    }
    return {clean, name, type};
}
